/**
 * The live preview next to the editor: the entry's page on the preview site in a frame, with what is being typed
 * patched in right away (see lib/preview.ts and public/preview-bridge.js). The page is shown at desktop width and
 * scaled to fit, or at phone width.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';
import type { Data, Field } from '@hotpot/core';
import { useT } from '../i18n.js';
import { locatorFor, mapScroll, previewDiff, syncPoints, valueAt, type Locator, type Span } from '../lib/preview.js';

const DESKTOP = 1440;
const PHONE = 390;
/** Where a field and its part of the page line up: this far down the visible form and the preview. */
const READING_LINE = 0.4;

export function LivePreview({
  url,
  fields,
  base,
  current,
  imageSrc,
  reloadKey,
  waiting,
  demo = false,
  form,
  scrollSync = true,
}: {
  /** The entry's page on the preview (or live) site. */
  url: string;
  fields: Field[];
  /** The entry as the page was built. */
  base: Data;
  /** The form right now. */
  current: Data;
  /** A same-origin URL that shows an image value (a pending upload or the repo file). */
  imageSrc: (value: string) => string | undefined;
  /** Changes when the page should load again (a new build is ready). */
  reloadKey: number;
  /** A new build of the page is on its way. */
  waiting: boolean;
  /** The in-browser demo shows a copy of a page next to itself (its frames may have an opaque origin). */
  demo?: boolean;
  /** The form next to the preview: the two scroll together. */
  form: RefObject<HTMLElement | null>;
  /** false: the page never scrolls by itself (not with the form, not to the text being typed). */
  scrollSync?: boolean;
}) {
  const { t } = useT();
  const frame = useRef<HTMLIFrameElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const [device, setDevice] = useState<'desktop' | 'phone'>('desktop');
  const [size, setSize] = useState({ w: 800, h: 600 });
  const [ready, setReady] = useState(false);
  const readyRef = useRef(false);
  const [noBridge, setNoBridge] = useState(false);
  const [loads, setLoads] = useState(0);
  const target = useMemo(() => {
    try {
      const u = new URL(url, location.href);
      // The demo's copy of a page is a plain folder of files.
      if (demo && !/\.html$/.test(u.pathname)) u.pathname = u.pathname.replace(/\/?$/, '/index.html');
      return u;
    } catch {
      return null;
    }
  }, [url, demo]);
  const dataUrls = useRef(new Map<string, Promise<string>>());
  /** Patches sent last time: the first new one is what is being edited, and the page scrolls to it. */
  const sent = useRef(new Set<string>());

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // The page tells us it can be patched.
  useEffect(() => {
    setReady(false);
    readyRef.current = false;
    setNoBridge(false);
    const onMessage = (e: MessageEvent) => {
      if (e.source !== frame.current?.contentWindow || (!demo && e.origin !== target?.origin)) return;
      if (e.data?.type === 'hotpot:ready') {
        setReady(true);
        readyRef.current = true;
        setNoBridge(false);
        sync.current = { els: [], key: '', page: [], pageMax: 0, view: 0, aligned: false, quietUntil: 0 };
      }
      if (e.data?.type === 'hotpot:anchors' && Array.isArray(e.data.spans)) {
        sync.current.page = e.data.spans.map((x: unknown) =>
          Array.isArray(x) && typeof x[0] === 'number' && typeof x[1] === 'number' ? ([x[0], x[1]] as Span) : null,
        );
        sync.current.pageMax = Number(e.data.max) || 0;
        sync.current.view = Number(e.data.view) || 0;
        // The page starts where the form is (at the top when an entry is opened).
        if (!sync.current.aligned) {
          sync.current.aligned = true;
          formScrolled();
        }
      }
      if (e.data?.type === 'hotpot:scrolled' && typeof e.data.y === 'number') pageScrolled(e.data.y);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [target?.origin, reloadKey, loads]);

  const diff = useMemo(() => previewDiff(fields, base, current), [fields, base, current]);

  // Send the current differences (debounced while typing). Pictures go as data URLs: the page is another site and
  // can't load the CMS's own image addresses.
  useEffect(() => {
    if (!ready || !target) return;
    let alive = true;
    const timer = setTimeout(async () => {
      const patches = await Promise.all(
        diff.patches.map(async (p) => {
          if (p.kind !== 'image') return p;
          const src = imageSrc(p.value);
          if (!src) return null;
          let pending = dataUrls.current.get(src);
          if (!pending) {
            pending = toDataUrl(src);
            dataUrls.current.set(src, pending);
          }
          return { kind: 'image' as const, from: p.from, to: await pending.catch(() => '') };
        }),
      );
      if (!alive) return;
      const list = patches.filter((p): p is NonNullable<typeof p> => !!p && p.to !== '');
      const keys = list.map((p) => `${p.kind}:${p.from}`);
      // `focus` makes the page scroll to the text being typed; with scrollSync off the page stays where it is.
      const focus = scrollSync ? keys.findIndex((k, i) => !sent.current.has(`${k}=${list[i]!.to}`)) : -1;
      sent.current = new Set(keys.map((k, i) => `${k}=${list[i]!.to}`));
      frame.current?.contentWindow?.postMessage(
        { type: 'hotpot:patch', patches: list, focus },
        demo ? '*' : target.origin,
      );
    }, 120);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [diff, ready, target, imageSrc]);

  // Scrolling together. Fields of the form whose value is shown on the page (a text, a picture) are paired with
  // where the page shows it (the page measures that: `hotpot:anchors`), and scroll positions in between are mapped
  // linearly. The side being scrolled by the visitor leads; the other follows without echoing back.
  const sync = useRef({ els: [] as HTMLElement[], key: '', page: [] as (Span | null)[], pageMax: 0, view: 0, aligned: false, quietUntil: 0 });
  const currentRef = useRef(current);
  currentRef.current = current;
  const post = (msg: unknown) => {
    if (target) frame.current?.contentWindow?.postMessage(msg, demo ? '*' : target.origin);
  };
  /** Pairs of scroll positions: the form's when a field's top or bottom is at the reading line, the page's when its part's is. */
  const points = () => {
    const { els, page, pageMax, view } = sync.current;
    const top = box.current?.getBoundingClientRect().top ?? 0;
    const formLine = top + (window.innerHeight - top) * READING_LINE;
    const pageLine = view * READING_LINE;
    const form = els.map((el): Span => {
      const r = el.getBoundingClientRect();
      return [r.top + window.scrollY - formLine, r.bottom + window.scrollY - formLine];
    });
    const shown = page.map((p): Span | null => (p ? [p[0] - pageLine, p[1] - pageLine] : null));
    return syncPoints(form, shown, document.documentElement.scrollHeight - window.innerHeight, pageMax);
  };
  const formScrolled = () => {
    if (!scrollSync || !readyRef.current || !sync.current.pageMax || performance.now() < sync.current.quietUntil) return;
    const { xs, ys } = points();
    post({ type: 'hotpot:scroll', y: Math.round(mapScroll(window.scrollY, xs, ys)) });
  };
  const pageScrolled = (y: number) => {
    if (!scrollSync || !sync.current.pageMax) return;
    const { xs, ys } = points();
    sync.current.quietUntil = performance.now() + 150; // the form's own scroll events from this aren't the visitor's
    window.scrollTo({ top: Math.round(mapScroll(y, ys, xs)), behavior: 'instant' });
  };

  // Tell the page what to measure: the innermost fields with something to find on the page.
  useEffect(() => {
    if (!ready || !scrollSync) return;
    const collect = () => {
      const root = form.current;
      if (!root) return;
      const els: HTMLElement[] = [];
      const targets: Locator[] = [];
      for (const el of root.querySelectorAll<HTMLElement>('.field[id^="f-"]')) {
        if (el.querySelector('.field[id^="f-"]')) continue;
        const l = locatorFor(valueAt(currentRef.current, el.id.slice(2)));
        if (!l) continue;
        els.push(el);
        targets.push(l);
      }
      sync.current.els = els;
      const key = JSON.stringify(targets);
      if (key !== sync.current.key) {
        sync.current.key = key;
        post({ type: 'hotpot:sync', targets });
      }
    };
    const timer = setTimeout(collect, 300);
    // Opening or closing list items changes which fields are there.
    let pending = 0;
    const ro = new ResizeObserver(() => {
      clearTimeout(pending);
      pending = window.setTimeout(collect, 200);
    });
    if (form.current) ro.observe(form.current);
    return () => {
      clearTimeout(timer);
      clearTimeout(pending);
      ro.disconnect();
    };
  }, [ready, current, target, demo, scrollSync]);

  // The form scrolls: the page follows.
  useEffect(() => {
    if (!ready || !scrollSync) return;
    let frameId = 0;
    const onScroll = () => {
      if (frameId) return;
      frameId = requestAnimationFrame(() => {
        frameId = 0;
        formScrolled();
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(frameId);
    };
  }, [ready, target, demo, scrollSync]);

  const width = device === 'desktop' ? DESKTOP : PHONE;
  const scale = Math.min(1, size.w / width);
  const height = size.h / scale;

  if (!target) return null;
  return (
    <div className="live-preview">
      <div className="lp-bar">
        <div className="lang-tabs" role="tablist" aria-label={t('livePreview')}>
          <button className={device === 'desktop' ? 'on' : ''} aria-selected={device === 'desktop'} role="tab" onClick={() => setDevice('desktop')}>
            {t('lpDesktop')}
          </button>
          <button className={device === 'phone' ? 'on' : ''} aria-selected={device === 'phone'} role="tab" onClick={() => setDevice('phone')}>
            {t('lpPhone')}
          </button>
        </div>
        <span className="lp-note">
          {waiting ? t('lpBuilding') : noBridge ? t('lpNoBridge') : diff.needsSave ? t('lpNeedsSave') : ready ? t('lpLive') : ''}
        </span>
        <button className="link-btn" onClick={() => setLoads((n) => n + 1)}>
          {t('lpReload')}
        </button>
        <a className="link-btn" href={target.href} target="_blank" rel="noopener noreferrer">
          {t('lpOpen')} ↗
        </a>
      </div>
      <div className="lp-frame" ref={box}>
        <iframe
          key={`${reloadKey}-${loads}`}
          ref={frame}
          title={t('livePreview')}
          src={target.href}
          onLoad={() => setTimeout(() => setNoBridge(!readyRef.current), 4000)}
          style={{
            width,
            height,
            transform: `scale(${scale})`,
            left: device === 'phone' ? Math.max(0, (size.w - width * scale) / 2) : 0,
          }}
          // The page runs its own scripts but can't reach the CMS (another origin) or open pop-ups over it.
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          referrerPolicy="no-referrer"
        />
      </div>
    </div>
  );
}

async function toDataUrl(src: string): Promise<string> {
  if (src.startsWith('data:')) return src;
  const blob = await (await fetch(src, { credentials: 'same-origin' })).blob();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}
